import type { Result } from '../ai/contracts';

/*
 * A layer document (Layers with Live Text — Bria's ad delayer, through nodes-back's layerize run)
 * as Artwork layers: a group over the source picture, holding its pictures, its flat colours and
 * its TEXT as text — the words, the face, the size, the colour, the spacing — so a flat design
 * comes back editable. Its boxes are in the document's canvas pixels; the group sits where the
 * source picture is and scales them to its frame.
 */

export interface LayerBox { x: number; y: number; w: number; h: number }
export interface DocumentLayer {
  id?: string; kind: 'image' | 'text' | 'fill'; name?: string; result?: number; bbox: LayerBox; z: number; visible?: boolean;
  fit?: { mode?: 'fill' | 'cover' | 'contain' };
  text?: string;
  /** The words wrap inside the box rather than standing on one line each (Layerize's lines). */
  wrap?: boolean;
  style?: { fontFamily?: string; fontWeight?: number; fontSizePx?: number; color?: string; letterSpacingPx?: number; lineHeight?: number;
    align?: 'left' | 'center' | 'right'; alignY?: 'top' | 'middle' | 'bottom'; uppercase?: boolean; italic?: boolean; underline?: boolean };
  fill?: string;
}
export interface LayerDocument { canvas: { w: number; h: number } | null; fonts?: string[]; layers: DocumentLayer[] }

/** '#rrggbb', '#rgb', '#rrggbbaa' or 'rgb(a)(…)' → 0–1 channels; black when unreadable. */
export function colourOf(text: string | undefined): { r: number; g: number; b: number; a: number } {
  const s = String(text || '').trim();
  const hex = /^#([0-9a-f]{3,8})$/i.exec(s)?.[1];
  if (hex) {
    const full = hex.length <= 4 ? hex.split('').map(c => c + c).join('') : hex;
    const n = (i: number) => parseInt(full.slice(i, i + 2), 16) / 255;
    return { r: n(0), g: n(2), b: n(4), a: full.length >= 8 ? n(6) : 1 };
  }
  const rgb = /^rgba?\(([^)]+)\)$/i.exec(s)?.[1]?.split(/[,\s/]+/).filter(Boolean).map(Number);
  if (rgb && rgb.length >= 3 && rgb.slice(0, 3).every(Number.isFinite)) return { r: rgb[0]! / 255, g: rgb[1]! / 255, b: rgb[2]! / 255, a: Number.isFinite(rgb[3]) ? rgb[3]! : 1 };
  return { r: 0, g: 0, b: 0, a: 1 };
}

const GENERIC = /^(?:serif|sans-serif|monospace|cursive|fantasy|system-ui|ui-[a-z-]+)$/i;
const stackOf = (value: string | undefined) => String(value ?? '').split(',').map((part) => part.trim().replace(/^(['"])(.*)\1$/, '$2').trim()).filter(Boolean);

/** The faces a CSS stack names, in order ("'Anton',sans-serif" → Anton): what a text layer and the font service take one of. */
export function familiesOf(value: string | undefined): string[] {
  return stackOf(value).filter((family) => !GENERIC.test(family));
}

/** The face a layer document's text is set in, as one family name: the stack's first. Empty → Inter. */
export function familyOf(value: string | undefined): string {
  return familiesOf(value)[0] ?? 'Inter';
}

/** The face standing in when none of a stack is to be had: of the kind the stack's generic asks for. */
function standInFor(value: string | undefined): string {
  const generic = stackOf(value).find((family) => GENERIC.test(family))?.toLowerCase();
  return generic === 'serif' ? 'Lora' : generic === 'monospace' ? 'Roboto Mono' : generic === 'cursive' ? 'Caveat' : 'Inter';
}

const solid = (colour: string | undefined) => ({ type: 'solid', color: colourOf(colour), visible: true, opacity: 1, blendMode: 'normal' });

/**
 * The create ops for a document, under `parentRef` (a "$ref" or node id), `unit` document units
 * per canvas pixel. Bottom first (by `z`). Text refs are returned so the canvas can measure them.
 */
export function documentOps(doc: LayerDocument, pictures: readonly Result[], parentRef: string, unit: number): { ops: unknown[]; texts: string[]; boxes: Record<string, { x: number; w: number; align: 'start' | 'center' | 'end' }> } {
  const ops: unknown[] = [], texts: string[] = [];
  const boxes: Record<string, { x: number; w: number; align: 'start' | 'center' | 'end' }> = {};
  const layers = [...doc.layers].filter(layer => layer && layer.bbox).sort((a, b) => (a.z ?? 0) - (b.z ?? 0));
  layers.forEach((layer, index) => {
    const ref = `doc${index}`;
    const box = { x: layer.bbox.x * unit, y: layer.bbox.y * unit, w: Math.max(1, layer.bbox.w * unit), h: Math.max(1, layer.bbox.h * unit) };
    const common = { name: (layer.name || (layer.kind === 'text' ? layer.text?.slice(0, 40) : undefined) || `Layer ${index + 1}`).slice(0, 200), transform: [1, 0, 0, 1, box.x, box.y], ...(layer.visible === false ? { visible: false } : {}) };
    if (layer.kind === 'image') {
      const picture = typeof layer.result === 'number' ? pictures[layer.result] : undefined;
      if (!picture) return;
      ops.push({ op: 'create', ref, parentId: parentRef, position: 'front', node: { type: 'image', ...common, assetId: picture.mediaAssetId, width: box.w, height: box.h, fit: layer.fit?.mode === 'contain' ? 'fit' : 'crop' } });
    } else if (layer.kind === 'fill') {
      ops.push({ op: 'create', ref, parentId: parentRef, position: 'front', node: { type: 'rect', ...common, width: box.w, height: box.h, fills: [solid(layer.fill)], strokes: [] } });
    } else if (layer.kind === 'text' && layer.text) {
      const style = layer.style ?? {};
      const lineHeight = Number(style.lineHeight);
      ops.push({ op: 'create', ref, parentId: parentRef, position: 'front', node: {
        // One line sizes to its words (a face that measures a little wider must not wrap "10" into
        // "1" over "0"); the canvas puts it back on its box by its alignment once measured. Words that
        // wrap (`wrap`, a Flow Layer node's text, or their own newlines) keep the box's width and grow down.
        type: 'text', ...common, runs: [{ text: layer.text }], sizing: layer.wrap === true || layer.text.includes('\n') ? 'auto-height' : 'auto-width', width: box.w, height: box.h,
        align: style.align === 'center' ? 'center' : style.align === 'right' ? 'end' : 'start',
        verticalAlign: style.alignY === 'middle' ? 'middle' : style.alignY === 'bottom' ? 'bottom' : 'top',
        style: {
          fontFamily: familyOf(style.fontFamily),
          fontWeight: Math.max(100, Math.min(900, Math.round(Number(style.fontWeight) || 400))),
          fontStyle: style.italic ? 'italic' : 'normal',
          fontSize: Math.max(1, Math.min(10_000, (Number(style.fontSizePx) || 16) * unit)),
          lineHeight: Number.isFinite(lineHeight) && lineHeight > 0 ? (lineHeight <= 5 ? { unit: 'percent', value: Math.round(lineHeight * 100) } : { unit: 'px', value: lineHeight * unit }) : 'auto',
          letterSpacing: { unit: 'px', value: (Number(style.letterSpacingPx) || 0) * unit },
          fills: [solid(style.color)],
          decoration: style.underline ? 'underline' : 'none',
          textCase: style.uppercase ? 'upper' : 'none',
        },
      } });
      texts.push(ref);
      boxes[ref] = { x: box.x, w: box.w, align: style.align === 'center' ? 'center' : style.align === 'right' ? 'end' : 'start' };
    }
  });
  return { ops, texts, boxes };
}

/**
 * The faces a document's text is set in, put in the file by the one who places it. A model names a face from a picture and can
 * name one the font lookup does not have (Bria: "Helvetica Neue", a stack, a guess), which was refused and dropped in silence:
 * the layer kept a name the file has no font for. Here each text takes the first face of its stack that `resolve` has (the
 * file's brand, the workspace's Flobi Fonts, Google: fontFinder), then the brand's own face (`preferred`), then a
 * stand-in of the same kind, and the layer is set in what was found; what changed is said (`swapped`, `fontsLine`). The font
 * service down leaves every name as it was, with nothing to say about the fonts.
 */
export async function chooseFonts<Font extends { family: string }>(doc: LayerDocument, resolve: (family: string, weights: number[], italic: boolean) => Promise<Font>, preferred: readonly string[] = []): Promise<{ document: LayerDocument; fonts: Font[]; swapped: { from: string; to: string }[] }> {
  const texts = doc.layers.filter((layer) => layer.kind === 'text');
  const weights = [...new Set(texts.map((layer) => Math.round(Number(layer.style?.fontWeight) || 400)))];
  const italic = texts.some((layer) => !!layer.style?.italic);
  const found = new Map<string, Font | null>();
  const faceOf = async (family: string) => {
    if (!found.has(family)) found.set(family, await resolve(family, weights, italic).catch(() => null));
    return found.get(family) ?? null;
  };
  const chosen = new Map<string, string>();
  const swapped: { from: string; to: string }[] = [];
  for (const stack of new Set(texts.map((layer) => layer.style?.fontFamily ?? ''))) {
    const asked = familiesOf(stack);
    let family: string | undefined;
    // `preferred`: the file's brand's face, before a stand-in that only matches the kind.
    for (const candidate of [...asked, ...preferred, standInFor(stack), 'Inter']) {
      const font = await faceOf(candidate);
      if (font) { family = font.family; break; }
    }
    chosen.set(stack, family ?? familyOf(stack));
    if (family && asked[0] && family.toLowerCase() !== asked[0].toLowerCase() && !swapped.some((one) => one.from === asked[0])) swapped.push({ from: asked[0], to: family });
  }
  const used = new Set(chosen.values());
  return {
    document: { ...doc, layers: doc.layers.map((layer) => (layer.kind === 'text' ? { ...layer, style: { ...layer.style, fontFamily: chosen.get(layer.style?.fontFamily ?? '') } } : layer)) },
    fonts: [...new Map([...found.values()].flatMap((font) => (font && used.has(font.family) ? [[font.family, font] as const] : []))).values()],
    swapped,
  };
}

/** What changed, in a sentence for the person; '' when every face was found. */
export function fontsLine(swapped: readonly { from: string; to: string }[]): string {
  return swapped.map(({ from, to }) => `${from} isn’t available, so that text is set in ${to}.`).join(' ');
}
